import { useState } from 'react'
import {
  Building2,
  Calendar,
  Eye,
  LogIn,
  Mail,
  MailCheck,
  MoreVertical,
  Shield,
  ShieldOff,
  KeyRound,
  User as UserIcon,
  Pencil,
} from 'lucide-react'
import type { User } from '../../hooks/use-users'

interface UserTableProps {
  users: User[]
  loading?: boolean
  onBlock?: (user: User) => void
  onUnblock?: (user: User) => void
  onImpersonate?: (user: User) => void
  onResetPassword?: (user: User) => void
  onViewDetails?: (user: User) => void
  onConfirmEmail?: (user: User) => void
  onEdit?: (user: User) => void
}

function getPrimaryMembership(user: User) {
  return user.organizations.find((membership) => membership.is_active) ?? user.organizations[0] ?? null
}

export function UserTable({
  users,
  loading = false,
  onBlock,
  onUnblock,
  onImpersonate,
  onResetPassword,
  onViewDetails,
  onConfirmEmail,
  onEdit,
}: UserTableProps) {
  const [openMenu, setOpenMenu] = useState<string | null>(null)

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca'
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca'
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getRoleBadge = (role: string) => {
    const badges: Record<string, { label: string; color: string }> = {
      admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
      professional: { label: 'Profissional', color: 'bg-blue-100 text-blue-700' },
      assistant: { label: 'Assistente', color: 'bg-gray-100 text-gray-700' },
    }
    const badge = badges[role] ?? { label: role, color: 'bg-gray-100 text-gray-700' }
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    )
  }

  const getStatusBadge = (user: User) => {
    if (user.is_blocked) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
          <ShieldOff className="w-3 h-3" />
          Bloqueado
        </span>
      )
    }

    if (!user.has_auth_account) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
          <Mail className="w-3 h-3" />
          Pendente
        </span>
      )
    }

    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
        <Shield className="w-3 h-3" />
        Ativo
      </span>
    )
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 rounded-full" />
            <div className="h-4 bg-gray-200 rounded w-48" />
          </div>
        </div>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-medium text-gray-900 mb-2">Nenhum usuário encontrado</h3>
          <p className="text-sm text-gray-500">Tente ajustar os filtros de busca</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-visible">
      <div className="divide-y divide-gray-100 md:hidden">
        {users.map((user) => {
          const primaryMembership = getPrimaryMembership(user)
          const activeOrganizations = user.organizations.filter((membership) => membership.is_active)

          return (
            <div key={user.profile_id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100">
                    <UserIcon className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{user.full_name || 'Sem nome'}</p>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-gray-500">
                      <Mail className="h-3 w-3 shrink-0" />
                      <span className="truncate">{user.email}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {getStatusBadge(user)}
                      {primaryMembership ? getRoleBadge(primaryMembership.role) : null}
                    </div>
                  </div>
                </div>

                <div className="relative shrink-0">
                  <button
                    onClick={() => setOpenMenu(openMenu === user.profile_id ? null : user.profile_id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <MoreVertical className="w-4 h-4 text-gray-500" />
                  </button>
                  {openMenu === user.profile_id && (
                    <>
                      <div className="fixed inset-0 z-[9998]" onClick={() => setOpenMenu(null)} />
                      <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                        {onViewDetails && (
                          <button
                            onClick={() => {
                              onViewDetails(user)
                              setOpenMenu(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            Ver detalhes
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => {
                              onEdit(user)
                              setOpenMenu(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-teal-600 hover:bg-teal-50 flex items-center gap-2"
                          >
                            <Pencil className="w-4 h-4" />
                            Editar usuário
                          </button>
                        )}
                        {onImpersonate && !user.is_blocked && user.has_auth_account && (
                          <button
                            onClick={() => {
                              onImpersonate(user)
                              setOpenMenu(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-purple-600 hover:bg-purple-50 flex items-center gap-2"
                          >
                            <LogIn className="w-4 h-4" />
                            Entrar como usuário
                          </button>
                        )}
                        {onResetPassword && !user.is_blocked && user.has_auth_account && (
                          <button
                            onClick={() => {
                              onResetPassword(user)
                              setOpenMenu(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2"
                          >
                            <KeyRound className="w-4 h-4" />
                            Definir nova senha
                          </button>
                        )}
                        {onConfirmEmail && user.has_auth_account && !user.email_confirmed_at && (
                          <button
                            onClick={() => {
                              onConfirmEmail(user)
                              setOpenMenu(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-teal-600 hover:bg-teal-50 flex items-center gap-2"
                          >
                            <MailCheck className="w-4 h-4" />
                            Confirmar Email
                          </button>
                        )}
                        {onBlock && !user.is_blocked && (
                          <button
                            onClick={() => {
                              onBlock(user)
                              setOpenMenu(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <ShieldOff className="w-4 h-4" />
                            Bloquear
                          </button>
                        )}
                        {onUnblock && user.is_blocked && (
                          <button
                            onClick={() => {
                              onUnblock(user)
                              setOpenMenu(null)
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                          >
                            <Shield className="w-4 h-4" />
                            Desbloquear
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Organização</p>
                  {activeOrganizations.length > 0 ? (
                    <>
                      <p className="mt-1 truncate text-sm font-medium text-gray-900">{primaryMembership?.name}</p>
                      {activeOrganizations.length > 1 && (
                        <p className="mt-0.5 text-xs text-gray-500">+{activeOrganizations.length - 1} vínculo(s)</p>
                      )}
                    </>
                  ) : (
                    <p className="mt-1 text-sm text-gray-400">Sem vínculo ativo</p>
                  )}
                </div>
                <div className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Último acesso</p>
                  <p className="mt-1 text-sm text-gray-700">{formatDateTime(user.last_sign_in_at)}</p>
                </div>
                <div className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Criado em</p>
                  <p className="mt-1 text-sm text-gray-700">{formatDate(user.created_at)}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="hidden overflow-x-auto overflow-y-visible md:block">
        <table className="w-full table-fixed">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-[34%] text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Usuário</th>
              <th className="w-[22%] text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Organizações</th>
              <th className="w-[16%] text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Função</th>
              <th className="w-[16%] text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
              <th className="hidden xl:table-cell w-[14%] text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Último Acesso</th>
              <th className="hidden 2xl:table-cell w-[12%] text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Criado Em</th>
              <th className="w-[12%] text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map((user) => {
              const primaryMembership = getPrimaryMembership(user)
              const activeOrganizations = user.organizations.filter((membership) => membership.is_active)

              return (
                <tr key={user.profile_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 shrink-0 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                        <UserIcon className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-gray-900">{user.full_name || 'Sem nome'}</p>
                        <div className="flex min-w-0 items-center gap-1 text-sm text-gray-500">
                          <Mail className="w-3 h-3 shrink-0" />
                          <span className="truncate">{user.email}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    {activeOrganizations.length > 0 ? (
                      <div className="space-y-1">
                        <div className="flex min-w-0 items-center gap-1.5 text-sm text-gray-600">
                          <Building2 className="w-4 h-4 shrink-0 text-gray-400" />
                          <span className="truncate">{primaryMembership?.name}</span>
                        </div>
                        {activeOrganizations.length > 1 && (
                          <p className="text-xs text-gray-500">
                            +{activeOrganizations.length - 1} organização(ões)
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Sem vínculo ativo</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {primaryMembership ? (
                      getRoleBadge(primaryMembership.role)
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4">{getStatusBadge(user)}</td>
                  <td className="hidden xl:table-cell px-4 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {formatDateTime(user.last_sign_in_at)}
                    </div>
                  </td>
                  <td className="hidden 2xl:table-cell px-4 py-4">
                    <span className="text-sm text-gray-600">{formatDate(user.created_at)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenu(openMenu === user.profile_id ? null : user.profile_id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-500" />
                      </button>
                      {openMenu === user.profile_id && (
                        <>
                          <div className="fixed inset-0 z-[9998]" onClick={() => setOpenMenu(null)} />
                          <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                            {onViewDetails && (
                              <button
                                onClick={() => {
                                  onViewDetails(user)
                                  setOpenMenu(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Eye className="w-4 h-4" />
                                Ver detalhes
                              </button>
                            )}
                            {onEdit && (
                              <button
                                onClick={() => {
                                  onEdit(user)
                                  setOpenMenu(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-teal-600 hover:bg-teal-50 flex items-center gap-2"
                              >
                                <Pencil className="w-4 h-4" />
                                Editar usuário
                              </button>
                            )}
                            {onImpersonate && !user.is_blocked && user.has_auth_account && (
                              <button
                                onClick={() => {
                                  onImpersonate(user)
                                  setOpenMenu(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-purple-600 hover:bg-purple-50 flex items-center gap-2"
                              >
                                <LogIn className="w-4 h-4" />
                                Entrar como usuário
                              </button>
                            )}
                            {onResetPassword && !user.is_blocked && user.has_auth_account && (
                              <button
                                onClick={() => {
                                  onResetPassword(user)
                                  setOpenMenu(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2"
                              >
                                <KeyRound className="w-4 h-4" />
                                Definir nova senha
                              </button>
                            )}
                            {onConfirmEmail && user.has_auth_account && !user.email_confirmed_at && (
                              <button
                                onClick={() => {
                                  onConfirmEmail(user)
                                  setOpenMenu(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-teal-600 hover:bg-teal-50 flex items-center gap-2"
                              >
                                <MailCheck className="w-4 h-4" />
                                Confirmar Email
                              </button>
                            )}
                            {onBlock && !user.is_blocked && (
                              <button
                                onClick={() => {
                                  onBlock(user)
                                  setOpenMenu(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <ShieldOff className="w-4 h-4" />
                                Bloquear
                              </button>
                            )}
                            {onUnblock && user.is_blocked && (
                              <button
                                onClick={() => {
                                  onUnblock(user)
                                  setOpenMenu(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                              >
                                <Shield className="w-4 h-4" />
                                Desbloquear
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
